import React, { useState } from "react";
import {
  Activity, Target, Layers, Newspaper, Sparkles, AlertTriangle,
  TrendingUp, Briefcase,
} from "lucide-react";
import SymbolChart from "@/components/SymbolChart";
import { fmtMoney, fmtPct, fmtNum, StatRow } from "./helpers";
import { AnalysisGEXCard } from "./AnalysisGEXCard";
import { AnalysisNewsCard } from "./AnalysisNewsCard";
import { IncomeCompassCard } from "./IncomeCompassCard";
import { PriceAlertsPanel } from "./PriceAlertsPanel";
import OptionsFlowCard from "./OptionsFlowCard";

const TickerHeader = ({ data }) => {
  const { quote } = data;
  return (
    <div className="rounded-xl border border-white/10 bg-[#070710] p-5 flex items-baseline justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 flex items-center gap-2">
          <span className="num">{data.symbol}</span>
          {data.sector && <span>· {data.sector}</span>}
        </div>
        <div className="text-[20px] font-semibold tracking-tight mt-0.5">{data.label || data.symbol}</div>
      </div>
      <div className="text-right">
        <div className="text-[26px] font-semibold num">${fmtNum(quote.price)}</div>
        <div
          className="text-[14px] num font-medium"
          style={{ color: quote.change_pct >= 0 ? "#00C805" : "#FF3B30" }}
        >
          {quote.change >= 0 ? "+" : ""}{fmtNum(quote.change)} ({fmtPct(quote.change_pct)})
        </div>
        <div className="text-[10px] text-white/45 num mt-0.5">
          Vol {(quote.volume / 1e6).toFixed(1)}M · Day {fmtNum(quote.day_low)}–{fmtNum(quote.day_high)}
        </div>
      </div>
    </div>
  );
};

const AIThesisCard = ({ ai }) => {
  if (!ai) return null;
  const biasColor =
    ai.directional_bias === "bullish" ? "#00C805"
    : ai.directional_bias === "bearish" ? "#FF3B30"
    : "#FFD60A";

  return (
    <div
      className="rounded-xl border border-[#3FE5DC]/22 bg-gradient-to-br from-[#0d1326] to-[#070710] p-5"
      data-testid="analysis-ai-block"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#3FE5DC]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#3FE5DC]">AI deep-dive thesis</span>
        <span
          className="ml-auto text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border"
          style={{ borderColor: `${biasColor}55`, color: biasColor }}
        >
          {ai.directional_bias} {ai.conviction_pct != null ? `· ${ai.conviction_pct}% conv` : ""}
        </span>
      </div>
      <p className="text-[13px] text-white/85 leading-relaxed">{ai.thesis}</p>

      {(ai.key_strengths?.length || ai.key_risks?.length) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {ai.key_strengths?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#00C805] mb-1.5 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Strengths
              </div>
              <ul className="space-y-1">
                {ai.key_strengths.map((s, i) => (
                  <li key={i} className="text-[11.5px] text-white/75 flex items-start gap-1.5">
                    <span className="text-[#00C805] shrink-0">▸</span><span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {ai.key_risks?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FF3B30] mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Risks
              </div>
              <ul className="space-y-1">
                {ai.key_risks.map((s, i) => (
                  <li key={i} className="text-[11.5px] text-white/75 flex items-start gap-1.5">
                    <span className="text-[#FF3B30] shrink-0">▸</span><span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {ai.watch_out && (
        <div className="mt-4 rounded-md border border-[#FFD60A]/25 bg-[#FFD60A]/5 p-2.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFD60A] mb-0.5">Watch out</div>
          <div className="text-[11.5px] text-white/75">{ai.watch_out}</div>
        </div>
      )}
    </div>
  );
};

const TradeIdeasCard = ({ ideas }) => {
  if (!ideas?.length) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-[#070710] p-5" data-testid="analysis-trade-ideas">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-[#A98CFF]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#A98CFF]">Trade ideas</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {ideas.slice(0, 3).map((t, i) => {
          const isShort = t.side?.toLowerCase().includes("short") || t.side?.toLowerCase().includes("put");
          return (
            <div
              key={i}
              className="rounded-md border border-white/10 bg-white/[0.02] p-3"
              data-testid={`trade-idea-${i}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] font-semibold text-white/90">{t.name}</span>
                <span
                  className="text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border"
                  style={{
                    borderColor: isShort ? "#FF3B3055" : "#00C80555",
                    color:       isShort ? "#FF3B30"   : "#00C805",
                  }}
                >
                  {t.side || "—"}
                </span>
              </div>
              {t.rationale && <p className="text-[11px] text-white/65 leading-snug mb-2">{t.rationale}</p>}
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10.5px] text-white/65 num">
                {t.entry_zone && <div>Entry: <span className="text-white/90">{t.entry_zone}</span></div>}
                {t.stop &&       <div>Stop: <span className="text-white/90">{t.stop}</span></div>}
                {t.target &&     <div>Target: <span className="text-white/90">{t.target}</span></div>}
                {t.timeframe &&  <div>TF: <span className="text-white/90">{t.timeframe}</span></div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TechnicalsCard = ({ technicals }) => (
  <div className="rounded-xl border border-white/10 bg-[#070710] p-4">
    <div className="flex items-center gap-2 mb-2">
      <Activity className="w-3.5 h-3.5 text-[#3FE5DC]" />
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#3FE5DC]">Technicals</span>
    </div>
    <StatRow label="RSI 14" value={fmtNum(technicals.rsi14)}
             sub={technicals.rsi14 > 70 ? "Overbought" : technicals.rsi14 < 30 ? "Oversold" : "Neutral"} />
    <StatRow label="MACD" value={fmtNum(technicals.macd?.macd)}
             sub={technicals.macd?.hist >= 0 ? "Bull cross" : "Bear cross"} />
    <StatRow label="SMA 20" value={fmtNum(technicals.sma20)} />
    <StatRow label="SMA 50" value={fmtNum(technicals.sma50)} />
    <StatRow label="SMA 200" value={fmtNum(technicals.sma200)} />
    <StatRow label="Boll. Upper" value={fmtNum(technicals.bbands?.upper)} />
    <StatRow label="Boll. Lower" value={fmtNum(technicals.bbands?.lower)} />
    <StatRow label="ATR 14" value={fmtNum(technicals.atr14)} />
    <StatRow label="Vol (30d ann.)" value={technicals.vol30_pct != null ? `${technicals.vol30_pct}%` : "—"} />
    <StatRow label="YTD" value={fmtPct(technicals.ytd_pct)} />
  </div>
);

const SRCard = ({ sr }) => (
  <div className="rounded-xl border border-white/10 bg-[#070710] p-4">
    <div className="flex items-center gap-2 mb-2">
      <Layers className="w-3.5 h-3.5 text-[#FFD60A]" />
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD60A]">Support / Resistance</span>
    </div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#FF3B30] mb-1">Resistance</div>
    {(sr.resistance || []).length === 0 && (
      <div className="text-[11px] text-white/40 mb-2">No clear pivot above</div>
    )}
    <ul className="mb-3">
      {(sr.resistance || []).map((lvl, i) => (
        <li key={i} className="flex justify-between py-1 border-b border-white/5 last:border-0">
          <span className="text-[11px] text-white/55">R{i + 1}</span>
          <span className="num text-[12.5px] font-medium" style={{ color: "#FF3B30" }}>${fmtNum(lvl)}</span>
        </li>
      ))}
    </ul>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#00C805] mb-1">Support</div>
    {(sr.support || []).length === 0 && (
      <div className="text-[11px] text-white/40">No clear pivot below</div>
    )}
    <ul>
      {(sr.support || []).map((lvl, i) => (
        <li key={i} className="flex justify-between py-1 border-b border-white/5 last:border-0">
          <span className="text-[11px] text-white/55">S{i + 1}</span>
          <span className="num text-[12.5px] font-medium" style={{ color: "#00C805" }}>${fmtNum(lvl)}</span>
        </li>
      ))}
    </ul>
  </div>
);

const SentimentOptionsCard = ({ sentiment, options }) => (
  <div className="rounded-xl border border-white/10 bg-[#070710] p-4">
    <div className="flex items-center gap-2 mb-2">
      <Newspaper className="w-3.5 h-3.5 text-[#A98CFF]" />
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A98CFF]">Sentiment + Options</span>
    </div>
    <StatRow
      label="Bias"
      value={
        <span style={{ color: sentiment.label === "bullish" ? "#00C805" : sentiment.label === "bearish" ? "#FF3B30" : "#A1A1AA" }}>
          {sentiment.label} ({sentiment.score >= 0 ? "+" : ""}{sentiment.score})
        </span>
      }
    />
    <StatRow label="52w range posn"
             value={sentiment.pct_of_52w_range != null ? `${sentiment.pct_of_52w_range}%` : "—"} />
    {(sentiment.factors || []).slice(0, 4).map((f, i) => (
      <div key={i} className="flex justify-between py-1 border-b border-white/5 last:border-0">
        <span className="text-[11px] text-white/55">{f.label}</span>
        <span className={`text-[11px] num ${f.bull ? "text-[#00C805]" : "text-[#FF3B30]"}`}>
          {f.bull ? "↗" : "↘"}
        </span>
      </div>
    ))}
    <div className="mt-3 border-t border-white/8 pt-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">Options · front-month</div>
      {options ? (
        <>
          <StatRow label="Put/Call OI" value={fmtNum(options.put_call_ratio)} />
          <StatRow label="IV30 ATM" value={options.iv30_atm_pct != null ? `${options.iv30_atm_pct}%` : "—"} />
          <StatRow label="Top call wall" value={`$${fmtNum(options.top_call_strike)}`}
                   sub={`${(options.top_call_oi / 1e3).toFixed(0)}K OI`} />
          <StatRow label="Top put wall" value={`$${fmtNum(options.top_put_strike)}`}
                   sub={`${(options.top_put_oi / 1e3).toFixed(0)}K OI`} />
        </>
      ) : (
        <div className="text-[11px] text-white/40">No options data</div>
      )}
    </div>
  </div>
);

const FundamentalsCard = ({ fundamentals }) => (
  <div className="rounded-xl border border-white/10 bg-[#070710] p-4">
    <div className="flex items-center gap-2 mb-3">
      <Briefcase className="w-3.5 h-3.5 text-white/65" />
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/65">Fundamentals</span>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-1">
      <StatRow label="Market cap" value={fmtMoney(fundamentals?.marketCap)} />
      <StatRow label="P/E (TTM)" value={fmtNum(fundamentals?.trailingPE)} />
      <StatRow label="Forward P/E" value={fmtNum(fundamentals?.forwardPE)} />
      <StatRow label="EPS (TTM)" value={fmtNum(fundamentals?.trailingEps)} />
      <StatRow label="Beta" value={fmtNum(fundamentals?.beta)} />
      <StatRow label="Div yield" value={fundamentals?.dividendYield != null ? `${(fundamentals.dividendYield * 100).toFixed(2)}%` : "—"} />
      <StatRow label="Profit margin" value={fundamentals?.profitMargins != null ? `${(fundamentals.profitMargins * 100).toFixed(1)}%` : "—"} />
      <StatRow label="ROE" value={fundamentals?.returnOnEquity != null ? `${(fundamentals.returnOnEquity * 100).toFixed(1)}%` : "—"} />
      <StatRow label="Rev growth" value={fundamentals?.revenueGrowth != null ? `${(fundamentals.revenueGrowth * 100).toFixed(1)}%` : "—"} />
      <StatRow label="EPS growth" value={fundamentals?.earningsGrowth != null ? `${(fundamentals.earningsGrowth * 100).toFixed(1)}%` : "—"} />
      <StatRow label="Debt/Equity" value={fmtNum(fundamentals?.debtToEquity)} />
      <StatRow label="Free cash flow" value={fmtMoney(fundamentals?.freeCashflow)} />
      <StatRow label="52w high" value={`$${fmtNum(fundamentals?.fiftyTwoWeekHigh)}`} />
      <StatRow label="52w low" value={`$${fmtNum(fundamentals?.fiftyTwoWeekLow)}`} />
      <StatRow label="Analysts target" value={`$${fmtNum(fundamentals?.targetMeanPrice)}`}
               sub={fundamentals?.numberOfAnalystOpinions ? `(${fundamentals.numberOfAnalystOpinions})` : ""} />
      <StatRow label="Short % float" value={fundamentals?.shortPercentOfFloat != null ? `${(fundamentals.shortPercentOfFloat * 100).toFixed(2)}%` : "—"} />
    </div>
    {fundamentals?.longBusinessSummary && (
      <div className="mt-4 pt-3 border-t border-white/8">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">Business summary</div>
        <p className="text-[11.5px] text-white/65 leading-relaxed">{fundamentals.longBusinessSummary}</p>
      </div>
    )}
  </div>
);

export const AnalysisContent = ({ data, headerExtra, onUpsellClick }) => {
  const [leaps, setLeaps] = useState(null);
  if (!data) return null;
  const { quote, technicals, support_resistance, fundamentals, options, sentiment, ai } = data;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <TickerHeader data={data} />
        </div>
        {headerExtra}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#070710] p-4">
        <SymbolChart
          symbol={data.symbol}
          period="6mo"
          height={280}
          adaptiveStop={leaps?.adaptive_exit}
        />
      </div>

      <IncomeCompassCard symbol={data.symbol} onData={setLeaps} />

      <AIThesisCard ai={ai} />
      <TradeIdeasCard ideas={ai?.trade_ideas} />

      <PriceAlertsPanel
        symbol={data.symbol}
        currentPrice={quote?.price}
        onUpsellClick={onUpsellClick}
      />

      <AnalysisGEXCard symbol={data.symbol} />

      <OptionsFlowCard symbol={data.symbol} />

      <AnalysisNewsCard symbol={data.symbol} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TechnicalsCard technicals={technicals} />
        <SRCard sr={support_resistance || {}} />
        <SentimentOptionsCard sentiment={sentiment || {}} options={options} />
      </div>

      <FundamentalsCard fundamentals={fundamentals || {}} />

      <div className="text-[10px] uppercase tracking-[0.18em] text-white/30 text-center">
        Generated {new Date(data.generated_at).toLocaleString()} · source: {data.source}
      </div>
    </div>
  );
};
